/**
 * The `/manage` menu (§ Beheer), outside the editions: platform-admins and
 * company-admins only, visibility per section driven by the same
 * `permissions` predicates as the buttons on the pages themselves — the API
 * stays the boundary, this is only what the menu offers to click.
 *
 * The pages are WP-13's; this WP delivers the menu's shape and its
 * visibility rules so WP-08 (`ManageLayout`) and WP-13 can build against it
 * without re-deriving the matrix from `PLAN-FASE1.md`.
 */

import type { Permissions } from '@/features/me/permissions';
import type { TranslationKey } from '@/i18n/types';

export interface ManageItem {
  id: string;
  labelKey: TranslationKey;
  to: string;
}

export interface ManageSection {
  id: string;
  labelKey: TranslationKey;
  visible: (permissions: Permissions) => boolean;
  items: readonly ManageItem[];
}

export const MANAGE_NAVIGATION: readonly ManageSection[] = [
  {
    id: 'companies',
    labelKey: 'manage.sections.companies.label',
    // Both an admin (all companies) and a company admin (their own) get this section.
    visible: (p) => p.canManage,
    items: [
      {
        id: 'companies',
        labelKey: 'manage.sections.companies.items.companies',
        to: '/manage/companies',
      },
      {
        id: 'pilots',
        labelKey: 'manage.sections.companies.items.pilots',
        to: '/manage/pilots',
      },
    ],
  },
  {
    id: 'people',
    labelKey: 'manage.sections.people.label',
    // admin only — a company admin links identities from a person page instead.
    visible: (p) => p.isAdmin,
    items: [
      { id: 'users', labelKey: 'manage.sections.people.items.users', to: '/manage/users' },
      {
        id: 'linkIdentities',
        labelKey: 'manage.sections.people.items.linkIdentities',
        to: '/manage/users/link',
      },
      { id: 'admins', labelKey: 'manage.sections.people.items.admins', to: '/manage/admins' },
    ],
  },
  {
    id: 'audit',
    labelKey: 'manage.sections.audit.label',
    // admin sees everything, a company admin the allowlisted subset of their own company.
    visible: (p) => p.canViewAudit,
    items: [{ id: 'audit', labelKey: 'manage.sections.audit.items.audit', to: '/manage/audit' }],
  },
  {
    id: 'subscriptions',
    labelKey: 'manage.sections.subscriptions.label',
    // admin only, same as `statistics.admin`/`requests.view`/`support.tickets.*`
    // above: `p.isAdmin` is the intended gate here, not a placeholder pending
    // a dedicated `subscriptions.admin` flag — the API's own role check
    // (SUBSCRIPTIONS-FRONTEND-BRIEFING.md § 2) is the real boundary, same as
    // everywhere else in this section.
    visible: (p) => p.isAdmin,
    items: [
      {
        id: 'config',
        labelKey: 'manage.sections.subscriptions.items.config',
        to: '/manage/subscriptions/config',
      },
      {
        id: 'products',
        labelKey: 'manage.sections.subscriptions.items.products',
        to: '/manage/subscriptions/products',
      },
      {
        id: 'userLedger',
        labelKey: 'manage.sections.subscriptions.items.userLedger',
        to: '/manage/subscriptions/user-ledger',
      },
    ],
  },
  {
    id: 'system',
    labelKey: 'manage.sections.system.label',
    visible: (p) => p.isAdmin,
    items: [
      {
        id: 'statistics',
        labelKey: 'manage.sections.system.items.statistics',
        to: '/manage/system/statistics',
      },
      {
        id: 'requests',
        labelKey: 'manage.sections.system.items.requests',
        to: '/manage/system/requests',
      },
    ],
  },
  {
    id: 'support',
    labelKey: 'manage.sections.support.label',
    // Platform-staff-only (PLAN-FASE3.md WP-01) — the API 403s anyone who
    // is not a platform admin, same gate as System above.
    visible: (p) => p.isAdmin,
    items: [
      {
        id: 'tickets',
        labelKey: 'manage.sections.support.items.tickets',
        to: '/manage/support/tickets',
      },
    ],
  },
  {
    id: 'overview',
    labelKey: 'manage.sections.overview.label',
    // Platform-staff-only (PLAN-FASE3.md WP-05, scope verkleind) — the API
    // 403s anyone who is not a platform admin, same gate as Support above.
    visible: (p) => p.isAdmin,
    items: [
      {
        id: 'overview',
        labelKey: 'manage.sections.overview.items.overview',
        to: '/manage/overview',
      },
    ],
  },
];

/**
 * Old paths that move under `/manage` (§ Beheer › Routes): `CompaniesPage`,
 * `CompanyDetailPage`, the company form pages, `RolePages`, `AuditPage`,
 * `LinkIdentitiesPage`, `ErasePage` and the admin part of `UsersPage`. Data
 * only — WP-13 relocates the actual pages; wiring these as live `<Navigate>`
 * routes before that move would either 404 (the `/manage/*` target does not
 * exist yet) or collide with the page still serving the old path today, so
 * `src/app/routes.tsx` does not mount them yet (see PLAN-FASE1-LOG.md /
 * Openstaande punten for this WP).
 */
export const LEGACY_MANAGE_REDIRECTS: ReadonlyMap<string, string> = new Map([
  ['/companies', '/manage/companies'],
  ['/admins', '/manage/admins'],
  ['/audit', '/manage/audit'],
]);

/**
 * `/dashboards/*` and `/metrics` do not move into Beheer — they disappear:
 * `/statistics/*` becomes elements on pages (§ Statistiek-elementen), not
 * pages of its own. Listed separately so a redirect target is never implied.
 */
export const REMOVED_PATH_PREFIXES: readonly string[] = ['/dashboards', '/metrics'];
